// ReviewCard - Hub card showing pending facts/aliases for review
// Three states: collapsed (header only), preview (1 fact with actions), full (review modal)

import React, { useState, useEffect } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Sparkles, ChevronRight, ChevronDown, ChevronUp, Quote, Check, X, Pencil, MoreVertical } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import { getPendingFacts, confirmFact, rejectFact, editFact } from '../../utils/memoryApi';
import useLocalStorage from '../../hooks/useLocalStorage';

const STORAGE_KEY = 'mabel-review-expanded';

export default function ReviewCard({ 
  pendingFactsCount = 0, 
  pendingAliasesCount = 0, 
  onOpenReviewModal,
  onReviewChanged
}) {
  const { getToken, isAuthenticated } = useKindeAuth();
  
  const [isExpanded, setIsExpanded] = useLocalStorage(STORAGE_KEY, false);

  // Preview fact and local count tracking
  const [previewFact, setPreviewFact] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [localFactsCount, setLocalFactsCount] = useState(pendingFactsCount);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  
  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const reviewCount = localFactsCount + pendingAliasesCount;

  // Sync local count with prop
  useEffect(() => {
    setLocalFactsCount(pendingFactsCount);
  }, [pendingFactsCount]);

  // Load preview fact when expanded and we have pending facts
  const loadPreviewFact = async () => {
    if (!isAuthenticated) return;
    
    setLoadingPreview(true);
    try {
      const token = await getToken();
      const result = await getPendingFacts(token, 1);
      if (result.facts && result.facts.length > 0) {
        setPreviewFact(result.facts[0]);
        setEditContent(result.facts[0].content);
      } else {
        setPreviewFact(null);
      }
    } catch (err) {
      console.error('Failed to load preview fact:', err);
      setPreviewFact(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (isExpanded && localFactsCount > 0) {
      loadPreviewFact();
    } else if (!isExpanded || localFactsCount === 0) {
      setPreviewFact(null);
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, localFactsCount, isAuthenticated]);

  // Toggle expand/collapse
  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setIsEditing(false);
  };

  // Open full review modal
  const handleOpenFull = () => {
    onOpenReviewModal();
  };

  // Approve fact
  const handleApprove = async () => {
    if (!previewFact || processing) return;
    
    setProcessing(true);
    try {
      const token = await getToken();
      await confirmFact(previewFact.id, token);
      
      // Update local count and load next fact
      setLocalFactsCount(prev => Math.max(0, prev - 1));
      if (localFactsCount > 1) {
        await loadPreviewFact();
      } else {
        setPreviewFact(null);
      }
      
      // Notify parent to refresh counts
      if (onReviewChanged) onReviewChanged();
    } catch (err) {
      console.error('Failed to approve fact:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Reject fact
  const handleReject = async () => {
    if (!previewFact || processing) return;
    
    setProcessing(true);
    try {
      const token = await getToken();
      await rejectFact(previewFact.id, token);
      
      // Update local count and load next fact
      setLocalFactsCount(prev => Math.max(0, prev - 1));
      if (localFactsCount > 1) {
        await loadPreviewFact();
      } else {
        setPreviewFact(null);
      }
      
      // Notify parent to refresh counts
      if (onReviewChanged) onReviewChanged();
    } catch (err) {
      console.error('Failed to reject fact:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Start editing
  const handleStartEdit = () => {
    setEditContent(previewFact.content);
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(previewFact?.content || '');
  };

  // Save edit and approve
  const handleSaveEdit = async () => {
    if (!previewFact || processing || !editContent.trim()) return;
    
    setProcessing(true);
    try {
      const token = await getToken();
      
      // Edit then confirm
      await editFact(previewFact.id, { content: editContent.trim() }, token);
      await confirmFact(previewFact.id, token);
      
      // Update local count and load next fact
      setLocalFactsCount(prev => Math.max(0, prev - 1));
      setIsEditing(false);
      if (localFactsCount > 1) {
        await loadPreviewFact();
      } else {
        setPreviewFact(null);
      }
      
      // Notify parent to refresh counts
      if (onReviewChanged) onReviewChanged();
    } catch (err) {
      console.error('Failed to save edit:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Bulk reject all pending facts
  const handleBulkReject = async () => {
    if (!isAuthenticated || bulkProcessing) return;
    
    setBulkProcessing(true);
    try {
      const token = await getToken();
      const response = await fetch('https://app-mabel-production.up.railway.app/api/user/facts/pending/reject-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to reject all pending facts');
      }

      // Reset local state
      setLocalFactsCount(0);
      setPreviewFact(null);
      setShowBulkConfirm(false);
      setMenuOpen(false);
      
      // Notify parent to refresh counts
      if (onReviewChanged) onReviewChanged();
    } catch (err) {
      console.error('Failed to bulk reject:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Don't render if nothing to review
  if (reviewCount === 0) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        padding: '14px 16px',
        background: ABLE_COLORS.neutral,
        border: 'none',
        borderRadius: ABLE_COLORS.cardRadius,
        boxShadow: ABLE_COLORS.cardShadow,
        textAlign: 'left'
      }}
    >
      {/* Header row - always visible */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Left side - icon + title + expand toggle */}
        <button
          onClick={handleToggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            flex: 1
          }}
        >
          <Sparkles size={20} color={ABLE_COLORS.warm} />
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            Review
          </span>
          <span style={{
            background: ABLE_COLORS.accent,
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            padding: '2px 6px',
            borderRadius: '8px'
          }}>
            {reviewCount}
          </span>
          {isExpanded ? (
            <ChevronUp size={16} color={ABLE_COLORS.textMuted} />
          ) : (
            <ChevronDown size={16} color={ABLE_COLORS.textMuted} />
          )}
        </button>

        {/* Right side - menu + review all */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
          {/* Three-dot menu */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              borderRadius: ABLE_COLORS.radiusSmall,
              color: ABLE_COLORS.textMuted
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.hoverBg}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          >
            <MoreVertical size={16} />
          </button>

          {/* Dropdown menu */}
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: 'white',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                boxShadow: ABLE_COLORS.cardShadow,
                zIndex: 1000,
                minWidth: '160px'
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  setShowBulkConfirm(true);
                }}
                disabled={localFactsCount === 0}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '13px',
                  color: localFactsCount === 0 ? ABLE_COLORS.textMuted : ABLE_COLORS.textPrimary,
                  cursor: localFactsCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: localFactsCount === 0 ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (localFactsCount > 0) e.target.style.background = ABLE_COLORS.hoverBg;
                }}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                Reject all pending
              </button>
            </div>
          )}

          {/* Review all button */}
          <button
            onClick={handleOpenFull}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              cursor: 'pointer',
              borderRadius: ABLE_COLORS.radiusSmall,
              color: ABLE_COLORS.textMuted,
              fontSize: '12px',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = ABLE_COLORS.hoverBg}
            onMouseLeave={(e) => e.target.style.background = 'none'}
          >
            <span>Review all</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Preview fact - only when expanded */}
      {isExpanded && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: `1px solid ${ABLE_COLORS.borderLight}`
        }}>
          {loadingPreview ? (
            <div style={{
              fontSize: '13px',
              color: ABLE_COLORS.textMuted,
              fontStyle: 'italic'
            }}>
              Loading...
            </div>
          ) : previewFact ? (
            <div>
              {/* Fact content or edit input */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '12px'
              }}>
                <Quote 
                  size={14} 
                  color={ABLE_COLORS.textMuted}
                  style={{ flexShrink: 0, marginTop: '2px' }}
                />
                <div style={{ flex: 1 }}>
                  {isEditing ? (
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '13px',
                        border: `1px solid ${ABLE_COLORS.border}`,
                        borderRadius: ABLE_COLORS.radiusSmall,
                        resize: 'vertical',
                        minHeight: '60px',
                        fontFamily: 'inherit',
                        lineHeight: '1.4'
                      }}
                      autoFocus
                    />
                  ) : (
                    <div style={{
                      fontSize: '13px',
                      color: ABLE_COLORS.textPrimary,
                      lineHeight: '1.4'
                    }}>
                      {previewFact.content}
                    </div>
                  )}
                  {previewFact.category && !isEditing && (
                    <div style={{
                      fontSize: '11px',
                      color: ABLE_COLORS.textMuted,
                      marginTop: '4px'
                    }}>
                      {previewFact.category}
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end'
              }}>
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      disabled={processing}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: ABLE_COLORS.textSecondary,
                        background: 'none',
                        border: `1px solid ${ABLE_COLORS.border}`,
                        borderRadius: ABLE_COLORS.radiusSmall,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.5 : 1
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={processing || !editContent.trim()}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'white',
                        background: ABLE_COLORS.primary,
                        border: 'none',
                        borderRadius: ABLE_COLORS.radiusSmall,
                        cursor: processing || !editContent.trim() ? 'not-allowed' : 'pointer',
                        opacity: processing || !editContent.trim() ? 0.5 : 1
                      }}
                    >
                      {processing ? 'Saving...' : 'Save & Approve'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleReject}
                      disabled={processing}
                      title="Reject"
                      style={{
                        padding: '6px 10px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: ABLE_COLORS.semantic.error,
                        background: 'none',
                        border: `1px solid ${ABLE_COLORS.semantic.error}40`,
                        borderRadius: ABLE_COLORS.radiusSmall,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <X size={14} />
                      <span>Reject</span>
                    </button>
                    <button
                      onClick={handleStartEdit}
                      disabled={processing}
                      title="Edit"
                      style={{
                        padding: '6px 10px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: ABLE_COLORS.textSecondary,
                        background: 'none',
                        border: `1px solid ${ABLE_COLORS.border}`,
                        borderRadius: ABLE_COLORS.radiusSmall,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Pencil size={14} />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={processing}
                      title="Approve"
                      style={{
                        padding: '6px 10px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'white',
                        background: ABLE_COLORS.semantic.success,
                        border: 'none',
                        borderRadius: ABLE_COLORS.radiusSmall,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Check size={14} />
                      <span>{processing ? '...' : 'Approve'}</span>
                    </button>
                  </>
                )}
              </div>

              {/* Show remaining count */}
              {reviewCount > 1 && !isEditing && (
                <div style={{
                  marginTop: '10px',
                  paddingTop: '10px',
                  borderTop: `1px solid ${ABLE_COLORS.borderLight}`,
                  fontSize: '12px',
                  color: ABLE_COLORS.textMuted
                }}>
                  +{reviewCount - 1} more to review
                </div>
              )}
            </div>
          ) : pendingAliasesCount > 0 ? (
            <div>
              <div style={{
                fontSize: '13px',
                color: ABLE_COLORS.textMuted,
                marginBottom: '8px'
              }}>
                {pendingAliasesCount} name{pendingAliasesCount !== 1 ? 's' : ''} to review
              </div>
              <button
                onClick={handleOpenFull}
                style={{
                  fontSize: '12px',
                  color: ABLE_COLORS.primary,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
              >
                Review names →
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Bulk reject confirmation dialog */}
      {showBulkConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => !bulkProcessing && setShowBulkConfirm(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: ABLE_COLORS.cardRadius,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: ABLE_COLORS.textPrimary,
                marginBottom: '8px'
              }}>
                Delete all pending facts?
              </div>
              <div style={{
                fontSize: '14px',
                color: ABLE_COLORS.textSecondary,
                lineHeight: '1.4'
              }}>
                This will delete {localFactsCount} pending fact{localFactsCount !== 1 ? 's' : ''}. This can't be undone.
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowBulkConfirm(false)}
                disabled={bulkProcessing}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary,
                  background: 'none',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: bulkProcessing ? 'not-allowed' : 'pointer',
                  opacity: bulkProcessing ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReject}
                disabled={bulkProcessing}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  background: ABLE_COLORS.semantic.error,
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: bulkProcessing ? 'not-allowed' : 'pointer',
                  opacity: bulkProcessing ? 0.5 : 1
                }}
              >
                {bulkProcessing ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
